import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface StripeTransaction {
  id: string;
  stripe_payment_intent_id: string;
  amount: number;
  currency: string;
  status: string;
  description: string;
  created_at: string;
  user_id?: string;
  plan?: string;
}

export function useStripeData() {
  const queryClient = useQueryClient();

  // Fetch Transactions from Supabase DB
  const { data: transactions, isLoading } = useQuery({
    queryKey: ["stripe_transactions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stripe_transactions")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as unknown as StripeTransaction[];
    },
  });

  // Sync Transactions from Stripe to DB
  const syncTransactions = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("stripe-sync", {
        body: { action: "sync_transactions" },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("交易記錄同步成功");
      queryClient.invalidateQueries({ queryKey: ["stripe_transactions"] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "未知錯誤";
      toast.error("同步失敗: " + message);
    },
  });

  // Fetch Stats (Directly from Edge Function for live balance)
  const { data: stats } = useQuery({
    queryKey: ["stripe_stats"],
    queryFn: async () => {
      // 1. Fetch Stripe Stats from Edge Function
      const { data: stripeData, error: stripeError } = await supabase.functions.invoke("stripe-sync", {
        body: { action: "get_stats" },
      });
      if (stripeError) throw stripeError;

      // 2. Fetch Gifted Users Count from Profiles
      const { count: giftedCount, error: profileError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .in('subscription_status', ['gifted', 'admin_gifted']);
      
      if (profileError) console.error("Error fetching gifted profiles:", profileError);

      // 3. Fetch Total Promo Code Discounts from Promo Code Usages
      // Note: We need to sum the 'discount_amount'. 
      // Since specific sum aggregation isn't directly available in simple select without RPC, 
      // we'll fetch all usages for this month for now (assuming volume isn't massive yet) 
      // or just fetch all time if needed. 
      // Requirement: "Sales Cost" usually implies monthly or total context depending on the dashboard.
      // RevenueStats shows MRR/ARR (Monthly/Yearly) and "Monthly Revenue".
      // Let's calculate "Monthly Sales Cost".
      
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: promoUsages, error: promoError } = await supabase
        .from('promo_code_usages')
        .select('discount_amount')
        .gte('created_at', startOfMonth.toISOString());

      if (promoError) console.error("Error fetching promo usages:", promoError);

      const monthlyDiscountAmount = promoUsages?.reduce((sum, usage: { discount_amount?: number | string | null }) => {
        return sum + (Number(usage.discount_amount) || 0);
      }, 0) || 0;
      const giftedCost = (giftedCount || 0) * 18; // Assuming $18/mo Pro Plan Price

      return {
        ...stripeData,
        monthly_sales_cost: giftedCost + monthlyDiscountAmount,
        gifted_count: giftedCount || 0,
        monthly_discount_amount: monthlyDiscountAmount
      };
    },
  });

  return {
    transactions,
    isLoading,
    syncTransactions,
    stats,
  };
}
